/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useDispatch} from 'react-redux';
// eslint-disable-next-line no-restricted-imports
import {useRouter} from 'next/compat/router';
import DashboardFrameManager from '@ion/charting/src/bis/dashboard/DashboardFrameManager';
import {AccountLink} from '@ion/core/src/types/entities';
import usePersistentIFrame from '@ion/dls/src/client/usePersistentIFrame';
import usePersistentSDKIFrame from '@ion/dls/src/client/usePersistentSDKIFrame';
import {setTrackingWorkingFavorite} from '@/components/filter/current/store';
import {
  setGlobalGroup
} from '@/store/actions/global-group-chooser';
import {
  resetFilterTreeNodes,
  setFilterTreeNodes
} from '@/store/actions/groups-filter';
import {clearAllSelections, resetSelectedAsset} from '@/store/actions/tracking';
import {useSelectAssetAndResets} from '../routes/tracking/hooks/sidesheet/useSelectAssetAndResets';
import {ReportTabMode} from '../types/redux';

/**
 *
 * React Hook to provide util functions for reset some states when global group changes
 *
 *  ```typescript
 *
 *   import {useSelectGlobalGroup} from '../../hooks/useSelectGlobalGroup';
 *
 *   const onSelectGlobalGroup = useSelectGlobalGroup();
 *
 *  ```
 */
export const useSelectGlobalGroup = () => {
  const dispatch                     = useDispatch();
  const [,,,, resetSideSheet]        = useSelectAssetAndResets();
  const route                        = useRouter();
  const {removePersistentIFrames}    = usePersistentIFrame({});
  const {removePersistentSDKIFrames} = usePersistentSDKIFrame({});

  const onSelectGlobalGroup = useCallback((group: AccountLink, nodes: any = [], mode?: ReportTabMode) => {
    DashboardFrameManager.clearAllFrameInstances();
    removePersistentIFrames();
    removePersistentSDKIFrames();
    dispatch(setFilterTreeNodes({nodes, mode}));
    dispatch(resetFilterTreeNodes({nodes: null}));
    dispatch(setGlobalGroup(group) as any);
    dispatch(resetSelectedAsset() as any);
    dispatch(clearAllSelections(route?.route === '/tracking') as any);
    // the current theme's filter drawer also keeps the favorite it is working
    // on - cleared with the filters, as the legacy theme's applied favorite
    // (in the store) is by `clearAllSelections` ...
    setTrackingWorkingFavorite(null);
    resetSideSheet();
  }, [resetSideSheet, route?.route]);

  return onSelectGlobalGroup;
};
